import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const PlanificacionContext = createContext();

export const usePlanificacion = () => {
  const context = useContext(PlanificacionContext);
  if (!context) {
    throw new Error('usePlanificacion debe usarse dentro de PlanificacionProvider');
  }
  return context;
};

export const PlanificacionProvider = ({ children }) => {
  const { user, isGuest } = useAuth();

  // Estado de la planificación
  const [area, setArea] = useState(null);
  const [grado, setGrado] = useState(null);
  const [libro, setLibro] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const [calendario, setCalendario] = useState([]);
  
  // Asignaciones
  const [asignaciones, setAsignaciones] = useState([]);
  
  // Semanas especiales
  const [semanasVacaciones, setSemanasVacaciones] = useState([]);
  const [semanasGestion, setSemanasGestion] = useState([]);
  
  // Periodos, unidades y ENFOQUES (Agrupado correctamente arriba)
  const [tipoPeriodo, setTipoPeriodo] = useState(null);
  const [periodos, setPeriodos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [enfoquesUnidades, setEnfoquesUnidades] = useState({}); // <-- ¡Corregido aquí!

  // Limpiar estado cuando cambia el usuario
  useEffect(() => {
    if (!user && !isGuest) {
      resetPlanificacion();
    }
  }, [user, isGuest]);

  const resetPlanificacion = () => {
    setArea(null);
    setGrado(null);
    setLibro(null);
    setSesiones([]);
    setCalendario([]);
    setAsignaciones([]);
    setSemanasVacaciones([]);
    setSemanasGestion([]);
    setTipoPeriodo(null);
    setPeriodos([]);
    setUnidades([]);
    setEnfoquesUnidades({}); // <-- Limpiamos enfoques también
  };

  const seleccionarCurso = (selectedArea, selectedGrado, selectedLibro) => {
    setArea(selectedArea);
    setGrado(selectedGrado);
    setLibro(selectedLibro);
  };

  const cargarSesiones = (listaSesiones) => setSesiones(listaSesiones);
  const cargarCalendario = (semanas) => setCalendario(semanas);

  const asignarSesion = (sesionId, semanaId) => {
    setAsignaciones(prev => {
      const otrasAsignaciones = prev.filter(a => a.semana_id !== semanaId);
      return [...otrasAsignaciones, { sesion_id: sesionId, semana_id: semanaId }];
    });
  };

  const removerSesion = (sesionId, semanaId) => {
    if (!semanaId) {
       setAsignaciones(prev => prev.filter(a => a.sesion_id !== sesionId));
       return;
    }
    setAsignaciones(prev => 
      prev.filter(a => !(a.sesion_id === sesionId && a.semana_id === semanaId))
    );
  };

  const toggleVacaciones = (semanaId) => {
    setSemanasVacaciones(prev => prev.includes(semanaId) ? prev.filter(id => id !== semanaId) : [...prev, semanaId]);
  };

  const toggleGestion = (semanaId) => {
    setSemanasGestion(prev => prev.includes(semanaId) ? prev.filter(id => id !== semanaId) : [...prev, semanaId]);
  };

  const getSesionDeSemana = (semanaId) => {
    const asignacion = asignaciones.find(a => a.semana_id === semanaId);
    if (!asignacion) return null;
    return sesiones.find(s => s.id === asignacion.sesion_id);
  };

  const getSemanaDeSesion = (sesionId) => {
    const asignacion = asignaciones.find(a => a.sesion_id === sesionId);
    if (!asignacion) return null;
    return calendario.find(s => s.id === asignacion.semana_id);
  };

  const definirTipoPeriodo = (tipo) => {
    setTipoPeriodo(tipo);
    setPeriodos([]);
    setUnidades([]);
  };

  const agregarPeriodo = (periodo) => setPeriodos(prev => [...prev, periodo]);
  const actualizarPeriodo = (periodoId, data) => setPeriodos(prev => prev.map(p => p.id === periodoId ? { ...p, ...data } : p));
  const agregarUnidad = (unidad) => setUnidades(prev => [...prev, unidad]);
  const actualizarUnidad = (unidadId, data) => setUnidades(prev => prev.map(u => u.id === unidadId ? { ...u, ...data } : u));
  const eliminarUnidad = (unidadId) => setUnidades(prev => prev.filter(u => u.id !== unidadId));

  const toggleEnfoqueUnidad = (unidadId, enfoqueId) => {
    setEnfoquesUnidades(prev => {
      const enfoquesActuales = prev[unidadId] || [];
      const existe = enfoquesActuales.includes(enfoqueId);
      
      return {
        ...prev,
        [unidadId]: existe 
          ? enfoquesActuales.filter(id => id !== enfoqueId)
          : [...enfoquesActuales, enfoqueId]
      };
    });
  };

  const getEstadisticas = () => {
    const totalSesiones = sesiones.length;
    const sesionesAsignadas = asignaciones.length;
    const semanasTotales = calendario.length;
    const semanasUsadas = new Set(asignaciones.map(a => a.semana_id)).size;
    const semanasVacacionesTotal = semanasVacaciones.length;
    const semanasGestionTotal = semanasGestion.length;

    return {
      totalSesiones,
      sesionesAsignadas,
      sesionesPendientes: totalSesiones - sesionesAsignadas,
      semanasTotales,
      semanasUsadas,
      semanasDisponibles: semanasTotales - semanasUsadas - semanasVacacionesTotal - semanasGestionTotal,
      semanasVacaciones: semanasVacacionesTotal,
      semanasGestion: semanasGestionTotal,
      porcentajeCompletado: totalSesiones > 0 ? Math.round((sesionesAsignadas / totalSesiones) * 100) : 0,
    };
  };

  // --- NUEVAS FUNCIONES PARA EL FLUJO AUTOMÁTICO ---

  // Para inyectar semanas de vacaciones desde la RM 501-2025-MINEDU
  const marcarSemanasVacaciones = (idsSemanas) => {
    // Filtramos posibles undefined/null por si alguna fecha no coincidió
    setSemanasVacaciones(idsSemanas.filter(id => id != null));
  };

  // Para inyectar semanas de gestión desde la RM 501-2025-MINEDU
  const marcarSemanasGestion = (idsSemanas) => {
    setSemanasGestion(idsSemanas.filter(id => id != null));
  };

  // Para el algoritmo de auto-distribución (Fase 3)
  const cargarAsignaciones = (nuevasAsignaciones) => {
    setAsignaciones(nuevasAsignaciones);
  };

  const value = {
    area, grado, libro, sesiones, calendario, asignaciones,
    semanasVacaciones, semanasGestion, tipoPeriodo, periodos, 
    unidades, enfoquesUnidades, // <-- Agregado al value
    
    resetPlanificacion, seleccionarCurso, cargarSesiones, cargarCalendario,
    asignarSesion, removerSesion, toggleVacaciones, toggleGestion,
    getSesionDeSemana, getSemanaDeSesion, definirTipoPeriodo,
    agregarPeriodo, actualizarPeriodo, agregarUnidad, actualizarUnidad,
    eliminarUnidad, toggleEnfoqueUnidad, getEstadisticas, // <-- Agregados al value

    // --> NUEVAS EXPORTACIONES:
    marcarSemanasVacaciones, marcarSemanasGestion, cargarAsignaciones
  };

  return (
    <PlanificacionContext.Provider value={value}>
      {children}
    </PlanificacionContext.Provider>
  );
};

export default PlanificacionContext;
